/***********************************************************************************************************************
 *
 * Version 1.0.0
 *
 * User: eliranm
 * Date: 26/3/15
 *
 * ---
 *
 *
 * A behavior serves to mix collapse/expand ability into views.
 *
 *
 * Dependencies
 * ============
 *
 * The owning view must have the collapsibleTrigger property on the ui hash. This property will point to the element
 * that should trigger the collapse/expand behavior with a mouse click (handled internally).
 *
 *
 * Usage
 * =====
 *
 *  var Collapsible = require('behaviors/collapsible/Collapsible');
 *  ...
 *  ui : {
 *      collapsibleTrigger: '.property-box-collapse'
 *  },
 *  ...
 *  behaviors: {
 *      Collapsible: {
 *          behaviorClass: Collapsible,
 *          toggleElements: ['.property-box-content', '> .double-divider-1'],
 *          silent: false
 *      }
 *  },
 *  ...
 *  onRender: function () {
 *      this.listenTo(this, Collapsible.EVENT.COLLAPSE, this._onCollapse);
 *      this.listenTo(this, Collapsible.EVENT.EXPAND, this._onExpand);
 *  }
 *
 *
 * Options
 * =======
 *
 *  toggleElements {array} (mandatory)
 *      An array of sizzle (CSS) selectors of elements that should be hidden with collapse and revealed with expand.
 *
 *  silent {boolean} (optional)
 *      Prevents triggering of events on the view event bus. Default is false.
 *
 *
 * API
 * ===
 *
 *  isExpanded()
 *      Checks if the view is currently expanded.
 *
 *
 * Events
 * ======
 *
 * Events are triggered via the owning view's event bus, on the following:
 *
 * - view collapsed
 * - view expanded
 *
 * Event names for registration are available under the Collapsible.EVENT namespace.
 *
 *
 ***********************************************************************************************************************
 */
define(function (require) {
    'use strict';

    var _ = require('underscore');
    var $ = require('jquery');
    var Marionette = require('marionette');
    var Logger = require('logger');


    // CONSTANTS

    var VERSION = '1.0.0',
        CLASSNAME = {
            EXPANDED: 'view-expanded',
            COLLAPSED: 'view-collapsed'
        },
        OPTION = {
            TOGGLE_ELEMENTS: 'toggleElements',
            SILENT: 'silent'
        },
        EVENT = {
            COLLAPSE: 'behavior:collapsible:collapse',
            EXPAND: 'behavior:collapsible:expand'
        };


    var Collapsible = Marionette.Behavior.extend({

        defaults: _.zipObject([OPTION.TOGGLE_ELEMENTS], [[]]),

        events: {
            // collapsibleTrigger is expected at the owning view's ui hash
            'click @ui.collapsibleTrigger': '_toggle'
        },


        // OVERRIDDEN

        initialize: function () {
            this.logger = Logger.getLogger('CoreComponents');
            this.logger.info('initializing...', 'Collapsible::initialize');
            this._validate();
            this.logger.info('finished initialization', 'Collapsible::initialize');
        },

        onRender: function () {
            this.logger.info('rendering', 'Collapsible::onRender');
            // set initial state as expanded (implementation will fall back to expanded state on initial invocation)
            this._toggle();
        },


        // PUBLIC API

        /**
         * Checks the collapse/expand state of the view.
         * @returns {boolean} A truthy value if the view is expanded, a falsey value if the view is collapsed, or
         * if the view's collapse/expand state has not initialized.
         */
        isExpanded: function () {
            // expanded state should be derived from both checking the expanded classname is present, and the collapsed
            // classname is missing - any other scenario should lead to setting the default state, which is expanded.
            return this.$el.is('.' + CLASSNAME.EXPANDED) && !this.$el.is('.' + CLASSNAME.COLLAPSED);
        },


        // PRIVATE

        _toggle: function () {
            var collapsing = this.isExpanded();
            this.logger.info('toggling [' + (collapsing ? 'collapse' : 'expand') + ']', 'Collapsible::_toggle');
            this._saveState(collapsing);
            this._toggleClasses(collapsing);
            this._toggleElements(!collapsing);
            this._restoreState(collapsing);
            this._triggerCollapseExpandEvents(collapsing);
        },

        _saveState: function (collapsing) {
            if (!collapsing) {
                return;
            }
            this.state = {
                // hash for saving any view styles which can only be retrieved/updated via jQuery's imperative api (and
                // not via .css(), .prop() etc.).
                // key: function name, value: function arguments.
                fn: {
                    // prevent width changes when hiding containers inside $el that effectively determine its implicit width
                    outerWidth: [this.$el.outerWidth(), true] // second argument is used to include margins as well
                }
            };
        },

        _restoreState: function (collapsing) {
            if (!collapsing || !this.state) {
                return;
            }
            if (this.state.fn) {
                _.each(this.state.fn, function (args, name) {
                    // invoking jQuery setter functions, resolves to e.g. this.$el.outerWidth.apply(this, args)
                    $.fn[name].apply(this.$el, args);
                }, this);
            }
        },

        _toggleClasses: function (collapsing) {
            this.$el.toggleClass(CLASSNAME.COLLAPSED, !!collapsing);
            this.$el.toggleClass(CLASSNAME.EXPANDED, !collapsing);
        },

        _toggleElements: function (hide) {
            this.$(this.options[OPTION.TOGGLE_ELEMENTS].join(',')).toggle(!!hide);
        },

        _triggerCollapseExpandEvents: function (collapsing) {
            if (this.options[OPTION.SILENT]) {
                return;
            }
            this.view.trigger(collapsing ? EVENT.COLLAPSE : EVENT.EXPAND);
        },

        _validate: function () {
            this.logger.info('validating options and view dependencies', 'Collapsible::_validate');
            this._validateView();
            this._validateOptions();
        },

        _validateView: function () {
            if (_.isUndefined(this.view.ui.collapsibleTrigger) || !_.isString(this.view.ui.collapsibleTrigger)) {
                this.logger.error('[collapsibleTrigger] was not found in the view ui hash, or is not a string');
            }
        },

        _validateOptions: function () {
            // mandatory
            if (!_.has(this.options, OPTION.TOGGLE_ELEMENTS) || !_.isArray(this.options[OPTION.TOGGLE_ELEMENTS]) ||
                _.isEmpty(this.options[OPTION.TOGGLE_ELEMENTS])) {
                this.logger.error('[' + OPTION.TOGGLE_ELEMENTS + '] selector-array was not found in options, or is empty');
            }
        }

    });


    // PUBLIC EXPOSURE

    Collapsible.EVENT = EVENT;

    Collapsible.VERSION = VERSION;


    return Collapsible;
});